import React, { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import StickyCta from "@/components/site/StickyCta";
import { pillarBySlug } from "@/data/platform";
import { INDUSTRIES } from "@/data/industries";
import { PillarIcon, ProductCard } from "@/components/platform/PlatformPrimitives";

export default function Pillar() {
  const { slug } = useParams();
  const pillar = pillarBySlug[slug];

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [slug]);

  if (!pillar) {
    return (
      <main className="bg-[#FDFCFB] min-h-screen">
        <Nav />
        <div className="pt-40 pb-32 max-w-2xl mx-auto px-6 text-center">
          <div className="caption mb-4">404 — Pillar not found</div>
          <h1 className="font-serif-display text-4xl tracking-tight">
            This pillar doesn't exist.
          </h1>
          <Link
            to="/platform"
            className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#1A1A1A] text-[#FDFCFB] text-sm"
          >
            <ArrowLeft className="w-4 h-4" /> All pillars
          </Link>
        </div>
        <Footer />
      </main>
    );
  }

  const liveCount = pillar.products.filter((p) => p.status === "live").length;
  const relatedIndustries = INDUSTRIES.filter((ind) =>
    ind.pillars?.some((bp) => bp.slug === pillar.slug)
  );

  return (
    <main className="bg-[#FDFCFB] text-[#1A1A1A] min-h-screen">
      <Nav />

      {/* Hero */}
      <section className="pt-32 md:pt-40 pb-16 max-w-7xl mx-auto px-6 md:px-12">
        <Link
          to="/platform"
          className="inline-flex items-center gap-2 text-sm text-[#6B6A68] hover:text-[#1A1A1A] mb-10 group"
        >
          <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
          All pillars
        </Link>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <div className="caption mb-4">Pillar · {pillar.name}</div>
            <div className="inline-flex items-center gap-3 mb-5">
              <div className="w-12 h-12 rounded-2xl bg-[#1A1A1A] text-[#FDFCFB] flex items-center justify-center">
                <PillarIcon name={pillar.icon} className="w-5 h-5" />
              </div>
              <span className="font-mono-tech text-[11px] tracking-[0.2em] uppercase text-[#6B6A68]">
                {pillar.products.length} products
                {liveCount > 0 && ` · ${liveCount} live`}
              </span>
            </div>
            <h1 className="font-serif-display text-[40px] sm:text-6xl md:text-7xl tracking-tighter leading-[1.02]">
              {pillar.tagline}
            </h1>
            <p className="mt-6 max-w-2xl text-[#6B6A68] text-base md:text-lg leading-relaxed">
              {pillar.summary}
            </p>
          </div>
        </div>
      </section>

      {/* Products grid */}
      <section className="py-16 md:py-20 bg-[#F5F4F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="caption mb-4">/ Products in this pillar</div>
              <h2 className="font-serif-display text-3xl md:text-4xl tracking-tight">
                Pick a product. <span className="italic">Get going.</span>
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {pillar.products.map((p) => (
              <motion.div
                key={p.slug}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4 }}
              >
                <ProductCard product={p} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Related industries */}
      {relatedIndustries.length > 0 && (
        <section className="py-16 md:py-20 bg-[#FDFCFB]">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="caption mb-4">/ Industries that use this</div>
            <h2 className="font-serif-display text-3xl md:text-4xl tracking-tight max-w-2xl mb-10">
              Already deployed across <span className="italic">{relatedIndustries.length} industries.</span>
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {relatedIndustries.map((ind) => (
                <Link
                  key={ind.slug}
                  to={`/industries/${ind.slug}`}
                  className="group p-4 rounded-xl border border-[#E8E6E1] hover:border-[#1A1A1A]/30 hover:bg-[#1A1A1A] hover:text-[#FDFCFB] transition-all"
                >
                  <div className="font-serif-display text-base md:text-lg tracking-tight">
                    {ind.name}
                  </div>
                  {ind.brand && (
                    <div className="font-mono-tech text-[10px] tracking-[0.18em] uppercase text-[#6B6A68] group-hover:text-[#FDFCFB]/60 mt-1">
                      {ind.brand}
                    </div>
                  )}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20 md:py-24 bg-[#1A1A1A] text-[#FDFCFB]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <div className="caption !text-[#FDFCFB]/50 mb-4">Talk to us</div>
            <h2 className="font-serif-display text-3xl md:text-5xl tracking-tight leading-[1.05]">
              Not sure where to start in <span className="italic">{pillar.name}?</span>
            </h2>
            <p className="mt-4 text-[#FDFCFB]/65 max-w-xl">
              30 minutes with our team, no slides. We map your needs to live
              products and tell you honestly what's not built yet.
            </p>
          </div>
          <div className="md:col-span-4 flex flex-col gap-3">
            <Link
              to="/#contact"
              className="inline-flex items-center justify-between gap-2 px-6 py-4 rounded-full bg-[#FDFCFB] text-[#1A1A1A] text-sm font-medium hover:bg-[#FDFCFB]/90 transition-all"
            >
              Book a Consultation
              <ArrowUpRight className="w-4 h-4" />
            </Link>
            <Link
              to="/platform"
              className="inline-flex items-center justify-between gap-2 px-6 py-4 rounded-full border border-[#FDFCFB]/30 text-sm hover:bg-[#FDFCFB] hover:text-[#1A1A1A] transition-all"
            >
              See all pillars
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <StickyCta />
    </main>
  );
}
